import { Button } from "@/components/ui/button";
import { ArrowLeft, User, Calendar, HelpCircle, LogOut, Settings, Home, Sparkles } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { useProviderAccess } from "@/hooks/useProviderAccess";
import { useFavorites } from "@/hooks/useFavorites";
import ProviderHeader from "@/components/ProviderHeader";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ConsumerHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { data: providerAccess, isLoading: accessLoading } = useProviderAccess();
  const { favorites } = useFavorites();

  // Get favorite provider for header branding
  const favoriteProvider = favorites.length > 0 ? favorites[0]?.providers : null;

  if (!accessLoading && providerAccess) {
    return <ProviderHeader />;
  }

  const showBackToDashboard = location.pathname.startsWith("/booking/") && location.pathname !== "/booking";

  const handleBackToDashboard = () => {
    if (location.pathname.includes("/booking/") && location.pathname.split("/").length === 4) {
      navigate(-1);
    } else {
      navigate("/dashboard");
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/auth");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const userName =
    user?.user_metadata?.first_name && user?.user_metadata?.last_name
      ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
      : user?.email || "User";

  const userEmail = user?.email || "";

  return (
    <header className="bg-background border-b border-border sticky top-0 left-0 right-0 z-50">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex justify-between items-center py-3">
          <div className="flex items-center space-x-4">
            {favoriteProvider && user ? (
              <Link to={`/provider/${favoriteProvider.slug}`} className="flex items-center gap-3">
                {favoriteProvider.image_url ? (
                  <img
                    src={favoriteProvider.image_url}
                    alt={favoriteProvider.name}
                    className="h-8 w-8 rounded-lg object-cover"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-lg bg-gray-200 flex items-center justify-center">
                    <span className="text-sm font-semibold text-gray-600">{favoriteProvider.name?.charAt(0)}</span>
                  </div>
                )}
                <span className="font-semibold text-foreground text-sm sm:text-base truncate max-w-[150px] sm:max-w-none">
                  {favoriteProvider.name}
                </span>
              </Link>
            ) : (
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-foreground flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-background" />
                </div>
                <span className="font-semibold text-lg tracking-tight">Wellsuites</span>
              </Link>
            )}
            {showBackToDashboard && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToDashboard}
                className="text-foreground hover:bg-foreground/5"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {providerAccess && user && (
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="font-medium rounded-full text-foreground hover:bg-foreground/5"
              >
                <Link to="/management">
                  <Settings className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Management</span>
                </Link>
              </Button>
            )}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-10 w-10 rounded-full p-0 hover:bg-foreground/5">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={user?.user_metadata?.avatar_url} alt={userName} />
                      <AvatarFallback className="bg-foreground text-background font-semibold text-sm">
                        {userName
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64 bg-card shadow-xl border border-border rounded-xl" align="end">
                  <div className="px-4 py-3 border-b border-border">
                    <p className="font-semibold text-foreground">{userName}</p>
                    <p className="text-sm text-muted-foreground">{userEmail}</p>
                  </div>

                  <DropdownMenuItem className="hover:bg-muted cursor-pointer py-3">
                    <Link
                      to={favoriteProvider ? `/provider/${favoriteProvider.slug}` : "/"}
                      className="w-full flex items-center"
                    >
                      <Home className="h-4 w-4 mr-3 text-muted-foreground" />
                      Home
                    </Link>
                  </DropdownMenuItem>

                  {providerAccess && (
                    <DropdownMenuItem className="hover:bg-muted cursor-pointer py-3 sm:hidden">
                      <Link to="/management" className="w-full flex items-center">
                        <Settings className="h-4 w-4 mr-3 text-muted-foreground" />
                        Management
                      </Link>
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuItem className="hover:bg-muted cursor-pointer py-3">
                    <Link to="/profile" className="w-full flex items-center">
                      <User className="h-4 w-4 mr-3 text-muted-foreground" />
                      Profile
                    </Link>
                  </DropdownMenuItem>

                  {!providerAccess && (
                    <DropdownMenuItem className="hover:bg-muted cursor-pointer py-3">
                      <Link to="/dashboard" className="w-full flex items-center">
                        <Calendar className="h-4 w-4 mr-3 text-muted-foreground" />
                        Bookings
                      </Link>
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuItem className="hover:bg-muted cursor-pointer py-3">
                    <Link to="/support" className="w-full flex items-center">
                      <HelpCircle className="h-4 w-4 mr-3 text-muted-foreground" />
                      Support
                    </Link>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  <DropdownMenuItem className="hover:bg-muted cursor-pointer py-3" onClick={handleSignOut}>
                    <LogOut className="h-4 w-4 mr-3 text-muted-foreground" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  asChild
                  variant="ghost"
                  className="font-semibold text-foreground hover:bg-foreground/5 rounded-full"
                >
                  <Link to="/auth">Log in</Link>
                </Button>
                <Button asChild className="rounded-full font-semibold">
                  <Link to="/auth">Sign up</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default ConsumerHeader;
